import React, { ReactElement } from 'react';
import { Translate, I18n } from 'react-redux-i18n';
import { Field } from 'react-final-form';
import ChipsList from './ChipsList';
import AutocompleteInput from '../inputs/autocompleteInput/AutocompleteInput';
import Button from '../Button';
import { AutocompleteSource } from '../inputs/autocompleteInput/AutocompleteSource';

type Props = {
  type: 'domaine' | 'lang'
  allElements: AutocompleteSource[]
  selectedElements: string[]
  setDirty?: () => void
  resetField: () => void
  updateField: (value: string[]) => void
  onFormChange?: () => void

  className: string
  customError?: string
  subtitleI18n: string
  placeholderI18n: string
};

type State = {
  list: AutocompleteSource[]
  selectedList: AutocompleteSource[]
  error: string
};

class ChipsListAutocomplete extends React.Component<Props, State> {
  state: State = {
    list: [],
    selectedList: [],
    error: '',
  };

  componentDidMount(): void {
    if (this.props.selectedElements) {
      this.setList();
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.selectedElements !== this.props.selectedElements) {
      this.setList();
    }
  }

  updateError = (label: string): void => {
    if (label && !this.contains(label)) {
      this.setState({ error: I18n.t(`PROFESSIONAL_INFORMATION.${this.props.type.toUpperCase()}_NOTINLIST`) });
    } else {
      this.setState({ error: '' });
    }
  };

  contains = (label: string): boolean => this.state.list.some((item) =>
    item.label === label || item.id === label);

  setList = (): void => {
    this.setState({
      selectedList: [],
      list: Array.from(this.props.allElements),
    }, () => {
      this.addAllToSelected(this.props.selectedElements);
    });
  };

  selectAutoSource = (label: string, fromId?: boolean): AutocompleteSource => {
    if (fromId) {
      return this.state.list.find(((lang) =>
        lang.id.toLowerCase() === label.toLowerCase()))
        || this.state.selectedList.find((lang) =>
          lang.id.toLowerCase() === label.toLowerCase());
    }
    return this.state.list.find(((lang) =>
      lang.label.toLowerCase() === label.toLowerCase()))
      || this.state.selectedList.find((lang) =>
        lang.label.toLowerCase() === label.toLowerCase());
  };

  addAllToSelected = (labels: string[]): void => {
    if (labels && labels.length && !labels.some((label) => !this.contains(label))) {
      const toAdd = labels.map((label) => this.selectAutoSource(label, true));
      this.setState((prevState) => {
        const add = prevState.selectedList.concat(toAdd);
        const remove = prevState.list;
        toAdd.forEach((item) => {
          remove.splice(remove.indexOf(item), 1);
        });
        return {
          selectedList: add,
          list: remove,
        };
      }, () => {
        this.props.updateField(this.state.selectedList
          .filter((item) => item).map((item) => item.id));
      });
    }
  };

  addOneToSelected = (label: string, callback?: () => void, fromId?: boolean): void => {
    if (label) {
      const toAdd = this.selectAutoSource(label, fromId);
      const index = this.state.list.indexOf(toAdd);
      if (index > -1) {
        this.setState((prevState) => {
          const add = prevState.selectedList;
          const remove = prevState.list;
          add.unshift(toAdd);
          remove.splice(index, 1);
          return {
            selectedList: add,
            list: remove,
          };
        }, () => {
          this.props.updateField(this.state.selectedList.map((item) => item.id));
          if (!fromId && this.props.setDirty) this.props.setDirty();
        });
      }
    }
  };

  removeFromSelected = (label: string): void => {
    if (label) {
      const toRemove = this.selectAutoSource(label);
      const index = this.state.selectedList.indexOf(toRemove);
      if (index > -1) {
        this.setState((prevState) => {
          const add = prevState.list;
          const remove = prevState.selectedList;
          add.push(toRemove);
          remove.splice(index, 1);
          return {
            list: add,
            selectedList: remove,
          };
        }, () => {
          this.props.updateField(this.state.selectedList.map((item) => item.id));
          if (this.props.setDirty) this.props.setDirty();
        });
      }
    }
  };

  handleItemClick = (label: string): void => {
    if (this.state.list.some((item) => item.label === label)) {
      this.props.resetField();
      this.addOneToSelected(label, () => {
        if (this.props.onFormChange) this.props.onFormChange();
      });
    }
  };

  render(): ReactElement {
    return (
      <div className="chips-autocomplete">
        <Field
          name={`${this.props.type}sSelect`}
          placeholder={I18n.t(this.props.placeholderI18n)}
          type="text"
          render={(props) => (
            <div className={this.props.className}>
              <div className="chips-list-selected">
                <div className="subtitle">
                  <Translate value={this.props.subtitleI18n} />
                </div>
                <ChipsList
                  removeChip={(label) => this.removeFromSelected(label)}
                  elements={this.state.selectedList}
                />
              </div>
              <AutocompleteInput
                input={props.input}
                meta={props.meta}
                selectSources={this.state.list}
                placeholder={I18n.t(this.props.placeholderI18n)}
                customError={this.state.error}
                onBlur={(label) => {
                  this.updateError(label);
                  if (this.props.onFormChange) this.props.onFormChange();
                }}
                onFocus={() => this.updateError('')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && props.input.value) {
                    event.preventDefault();
                    this.handleItemClick(props.input.value);
                  }
                }}
                onEnterSelect={(event, label: string) => {
                  event.preventDefault();
                  this.handleItemClick(label);
                }}
              >
                <Button
                  type="secondary"
                  onClick={() => this.handleItemClick(props.input.value)}
                >
                  <Translate value="actions.ADD" />
                </Button>
              </AutocompleteInput>
            </div>
          )}
        />
      </div>
    );
  }
}

export default ChipsListAutocomplete;
